import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import {
  applyRegulariseDateChange,
  applyRegulariseTimeChange,
  clampDateIsoToToday,
  computeDurationBetweenTimeInputs,
  getLoggedInEmployeeId,
  getMaxDateInputValue,
  getMaxTimeInputValueForDate,
  isFutureDateIso,
  loadRegularisationSessionHistory,
  resolveEmployeeDayChecks,
  saveRegularisationSessionEntry,
  toTimeInputValue,
  validateRegulariseCheckTimes,
} from "../../../../lib/attendanceDayChecks";
import { ATTENDANCE_PUNCH_UPDATED_EVENT } from "../../../../lib/attendancePunchStorage";

const HISTORY_UPDATED_EVENT = "hrms-regularisation-history-updated";

const emptyDayRecords = () => ({
  loading: false,
  hasRecords: false,
  checkIn: null,
  checkOut: null,
  totalHours: null,
  sources: [],
});

/**
 * Shared Regularise checks request form (Sidebar ESS + attendance calendar).
 */
export default function RegulariseChecksRequestModal({
  isOpen,
  onClose,
  darkMode = false,
  employeeId,
  employeeName = "Employee",
  initialDateIso = null,
  loggedInEmployeeId: loggedInEmployeeIdProp = null,
  onSubmit,
  submitLabel = "Submit Request",
  zIndexClass = "z-[10010]",
  embedded = false,
}) {
  const maxDateToday = useMemo(() => getMaxDateInputValue(), [isOpen]);
  const [dateIso, setDateIso] = useState("");
  const [inTime, setInTime] = useState("");
  const [outTime, setOutTime] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [formError, setFormError] = useState("");
  const [dayRecords, setDayRecords] = useState(emptyDayRecords);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyRefresh, setHistoryRefresh] = useState(0);

  const loggedInEmployeeId = loggedInEmployeeIdProp ?? getLoggedInEmployeeId();
  const maxTimeForDate = useMemo(
    () => getMaxTimeInputValueForDate(dateIso || maxDateToday),
    [dateIso, maxDateToday]
  );

  const dateLabel = useMemo(() => {
    if (!dateIso || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return "-";
    const [y, m, d] = dateIso.split("-");
    return `${d}-${m}-${y}`;
  }, [dateIso]);

  const previewHours = useMemo(
    () => computeDurationBetweenTimeInputs(inTime, outTime),
    [inTime, outTime]
  );

  const historyRows = useMemo(() => {
    if (!employeeId) return [];
    return loadRegularisationSessionHistory(employeeId);
  }, [employeeId, historyRefresh, isOpen]);

  const bumpHistory = () => setHistoryRefresh((n) => n + 1);

  useEffect(() => {
    if (!isOpen) return;
    const initial = initialDateIso ? clampDateIsoToToday(initialDateIso) : maxDateToday;
    setDateIso(initial);
    setInTime("");
    setOutTime("");
    setReason("");
    setNotes("");
    setFormError("");
    setDayRecords(emptyDayRecords());
    setShowHistoryModal(false);
  }, [isOpen, initialDateIso, maxDateToday]);

  useEffect(() => {
    if (!isOpen || !employeeId || !dateIso) return;
    if (isFutureDateIso(dateIso)) {
      setDayRecords(emptyDayRecords());
      return;
    }
    let cancelled = false;
    setDayRecords((prev) => ({ ...prev, loading: true }));
    const preferMyHistory =
      loggedInEmployeeId != null && String(employeeId) === String(loggedInEmployeeId);
    resolveEmployeeDayChecks(String(employeeId), dateIso, { preferMyHistory }).then((result) => {
      if (cancelled) return;
      setDayRecords({ loading: false, ...result });
      if (result.hasRecords) {
        const effective = clampDateIsoToToday(dateIso);
        const cinRaw = toTimeInputValue(result.checkIn) || "";
        const coutRaw = toTimeInputValue(result.checkOut) || "";
        const { time: cin, error: cinErr } = applyRegulariseTimeChange(
          effective,
          cinRaw,
          "Check-in"
        );
        const { time: cout, error: coutErr } = applyRegulariseTimeChange(
          effective,
          coutRaw,
          "Check-out"
        );
        setInTime(cin);
        setOutTime(cout);
        if (cinErr || coutErr) {
          setFormError(cinErr || coutErr || "");
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, employeeId, dateIso, loggedInEmployeeId]);

  useEffect(() => {
    if (!isOpen || !employeeId) return;
    const onPunch = (e) => {
      const punchEmp = String(e?.detail?.employeeId ?? "");
      if (punchEmp && punchEmp !== String(employeeId)) return;
      bumpHistory();
      if (dateIso && !isFutureDateIso(dateIso)) {
        setDayRecords((prev) => ({ ...prev, loading: true }));
        resolveEmployeeDayChecks(String(employeeId), dateIso, {
          preferMyHistory: String(employeeId) === String(loggedInEmployeeId),
        }).then((result) => setDayRecords({ loading: false, ...result }));
      }
    };
    const onHistory = (e) => {
      const id = String(e?.detail?.employeeId ?? "");
      if (!id || id === String(employeeId)) bumpHistory();
    };
    window.addEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
    window.addEventListener(HISTORY_UPDATED_EVENT, onHistory);
    return () => {
      window.removeEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
      window.removeEventListener(HISTORY_UPDATED_EVENT, onHistory);
    };
  }, [isOpen, employeeId, dateIso, loggedInEmployeeId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const effectiveDate = clampDateIsoToToday(dateIso || maxDateToday);
    const validation = validateRegulariseCheckTimes({
      dateIso: effectiveDate,
      inTime,
      outTime,
    });
    if (!validation.valid) {
      setFormError(validation.errors[0] || "Invalid check times.");
      return;
    }
    const reasonTrim = reason.trim();
    if (!reasonTrim) {
      setFormError("Please enter a reason for this request.");
      return;
    }
    setFormError("");

    const parts = effectiveDate.split("-");
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    const totalHours = previewHours || "";

    if (employeeId && Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
      saveRegularisationSessionEntry(employeeId, {
        dateIso: effectiveDate,
        year,
        month,
        day,
        checkIn: inTime.trim() || "--",
        checkOut: outTime.trim() || "--",
        totalHours,
        reason: reasonTrim,
        notes: notes.trim(),
      });
      bumpHistory();
    }

    onSubmit?.({
      date: effectiveDate,
      inTime: inTime.trim(),
      outTime: outTime.trim(),
      reason: reasonTrim,
      notes: notes.trim(),
      totalHours,
      year,
      month,
      day,
    });
  };

  if (!isOpen) return null;

  const inputCls =
    "mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-[#3C3C4C] dark:bg-[#1D1D1D]";

  const shellCls = darkMode
    ? "border-[#3C3C4C] bg-[#121212] text-gray-100"
    : "border-gray-200 bg-white text-black";

  const formBody = (
    <form
      onSubmit={handleSubmit}
      className={`space-y-3 ${embedded ? "min-h-0 flex-1 overflow-y-auto" : "max-h-[min(82vh,720px)] overflow-y-auto p-4"} ${embedded ? "" : "p-4"}`}
    >
            <p className={`text-[10px] ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              You cannot regularise a future date or future check-in/out time. Choose a date to see
              existing punches for the employee, then enter corrected times.
            </p>

            <label className={`block text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Date
              <input
                type="date"
                required
                max={maxDateToday}
                value={dateIso || maxDateToday}
                onChange={(e) => {
                  const { dateIso: nextDate, error } = applyRegulariseDateChange(e.target.value);
                  setFormError(error || "");
                  setDateIso(nextDate);
                  setInTime((t) => applyRegulariseTimeChange(nextDate, t, "Check-in").time);
                  setOutTime((t) => applyRegulariseTimeChange(nextDate, t, "Check-out").time);
                }}
                className={inputCls}
              />
            </label>

            {formError ? (
              <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                {formError}
              </p>
            ) : null}

            <div
              className={`rounded-md border px-3 py-2 text-xs ${
                dayRecords.hasRecords
                  ? darkMode
                    ? "border-emerald-900 bg-emerald-950/30 text-emerald-100"
                    : "border-emerald-200 bg-emerald-50/80 text-emerald-900"
                  : darkMode
                  ? "border-[#3C3C3C] bg-[#1a1a1a] text-gray-400"
                  : "border-gray-200 bg-gray-50 text-gray-600"
              }`}
            >
              {dayRecords.loading ? (
                <p>Loading check-in/out for {dateLabel}…</p>
              ) : dayRecords.hasRecords ? (
                <>
                  <p className="font-semibold">
                    These are the checks as per the records for the day ({dateLabel}):
                  </p>
                  <ul className="mt-1.5 space-y-0.5 tabular-nums">
                    <li>
                      <span className="font-medium">Check-in:</span> {dayRecords.checkIn ?? "—"}
                    </li>
                    <li>
                      <span className="font-medium">Check-out:</span> {dayRecords.checkOut ?? "—"}
                    </li>
                    {dayRecords.totalHours ? (
                      <li>
                        <span className="font-medium">Total hours:</span> {dayRecords.totalHours}
                      </li>
                    ) : null}
                  </ul>
                  {dayRecords.sources?.length > 0 ? (
                    <p className="mt-1 text-[10px] opacity-80">
                      Source:{" "}
                      {dayRecords.sources.includes("system") &&
                      dayRecords.sources.includes("punch")
                        ? "System records and navbar punch"
                        : dayRecords.sources.includes("system")
                        ? "System attendance records"
                        : "Navbar check-in/out"}
                    </p>
                  ) : null}
                </>
              ) : (
                <p>
                  No check-in/out records found in the system for {dateLabel}. Enter corrected times
                  below.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                In Time
                <input
                  type="time"
                  required
                  max={maxTimeForDate}
                  value={inTime}
                  onChange={(e) => {
                    const effective = clampDateIsoToToday(dateIso || maxDateToday);
                    const { time, error } = applyRegulariseTimeChange(
                      effective,
                      e.target.value,
                      "Check-in"
                    );
                    setFormError(error || "");
                    setInTime(time);
                  }}
                  onBlur={(e) => {
                    const effective = clampDateIsoToToday(dateIso || maxDateToday);
                    const { time, error } = applyRegulariseTimeChange(
                      effective,
                      e.target.value,
                      "Check-in"
                    );
                    if (time !== e.target.value) setInTime(time);
                    if (error) setFormError(error);
                  }}
                  className={inputCls}
                />
              </label>
              <label className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                Out Time
                <input
                  type="time"
                  required
                  max={maxTimeForDate}
                  value={outTime}
                  onChange={(e) => {
                    const effective = clampDateIsoToToday(dateIso || maxDateToday);
                    const { time, error } = applyRegulariseTimeChange(
                      effective,
                      e.target.value,
                      "Check-out"
                    );
                    setFormError(error || "");
                    setOutTime(time);
                  }}
                  onBlur={(e) => {
                    const effective = clampDateIsoToToday(dateIso || maxDateToday);
                    const { time, error } = applyRegulariseTimeChange(
                      effective,
                      e.target.value,
                      "Check-out"
                    );
                    if (time !== e.target.value) setOutTime(time);
                    if (error) setFormError(error);
                  }}
                  className={inputCls}
                />
              </label>
            </div>

            <p className={`text-[10px] ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              Total hours (reference):{" "}
              <span className={`font-semibold ${darkMode ? "text-gray-200" : "text-gray-800"}`}>
                {!inTime.trim() || !outTime.trim() ? "—" : previewHours || "—"}
              </span>
            </p>

            <label className={`block text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Reason
              <textarea
                required
                rows={3}
                value={reason}
                onChange={(e) => {
                  setFormError("");
                  setReason(e.target.value);
                }}
                className={inputCls}
                placeholder="Enter request reason..."
              />
            </label>

            <label className={`block text-xs ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              Notes (optional)
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className={inputCls}
                placeholder="Additional details"
              />
            </label>

            <div className="flex flex-wrap items-center gap-y-2 gap-x-3 min-h-[40px] pt-1">
              <div className="flex-1 min-w-[133px] flex justify-start items-center">
                <button
                  type="button"
                  onClick={() => setShowHistoryModal(true)}
                  className={`px-3 py-1.5 rounded text-sm font-medium ${
                    darkMode
                      ? "bg-gray-700 hover:bg-gray-600 text-gray-100"
                      : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                  }`}
                >
                  View history
                  {historyRows.length > 0 ? ` (${historyRows.length})` : ""}
                </button>
              </div>
              <div className="flex gap-2 justify-end flex-1 min-w-[200px]">
                <button
                  type="button"
                  onClick={onClose}
                  className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
                    darkMode
                      ? "border-[#3C3C4C] bg-[#1D1D1D] text-gray-200 hover:bg-[#2a2a2a]"
                      : "border-gray-300 bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!!formError}
                  className="rounded-md border border-redhalo-red bg-redhalo-red px-3 py-1.5 text-xs font-medium text-white hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitLabel}
                </button>
              </div>
            </div>
          </form>
  );

  if (embedded) {
    return (
      <>
        <div className="flex h-full min-h-0 flex-col">{formBody}</div>
        {showHistoryModal && (
        <div
          className="fixed inset-0 z-[10070] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={() => setShowHistoryModal(false)}
        >
          <div
            className={`w-full max-w-lg max-h-[min(76vh,532px)] overflow-hidden flex flex-col rounded-lg shadow-xl border ${shellCls}`}
            role="dialog"
            aria-labelledby="reg-history-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${
                darkMode ? "border-[#3C3C3C]" : "border-gray-200"
              }`}
            >
              <div>
                <h3
                  id="reg-history-title"
                  className={`text-base font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}
                >
                  Regularised check dates
                </h3>
                <p className={`text-xs mt-0.5 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  {employeeName}
                  {employeeId ? ` · ID ${employeeId}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className={`rounded p-1.5 ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                aria-label="Close history"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              {historyRows.length === 0 ? (
                <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  No regularised check dates yet. Submit a request to add an entry here.
                </p>
              ) : (
                <ul
                  className={`divide-y rounded-md border ${
                    darkMode ? "divide-[#3C3C3C] border-[#3C3C3C]" : "divide-gray-200 border-gray-200"
                  }`}
                >
                  {historyRows.map((row) => (
                    <li
                      key={`${row.year}-${row.month}-${row.day}-${row.id}`}
                      className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-xs tabular-nums"
                    >
                      <span
                        className={`min-w-[5.5rem] font-semibold ${
                          darkMode ? "text-gray-100" : "text-gray-900"
                        }`}
                      >
                        {row.label}
                      </span>
                      <span>
                        <span className={`font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                          In
                        </span>{" "}
                        {row.checkIn ?? "—"}
                      </span>
                      <span>
                        <span className={`font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                          Out
                        </span>{" "}
                        {row.checkOut ?? "—"}
                      </span>
                      <span>
                        <span className={`font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                          Hrs
                        </span>{" "}
                        {row.totalHours ?? "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={`fixed inset-0 ${zIndexClass} flex items-center justify-center bg-black/45 p-4`}
        role="presentation"
        onClick={onClose}
      >
        <div
          className={`w-full max-w-xl rounded-lg border shadow-xl ${shellCls}`}
          role="dialog"
          aria-labelledby="regularise-checks-request-title"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className={`flex items-center justify-between border-b px-4 py-3 ${
              darkMode ? "border-[#3C3C4C]" : "border-gray-200"
            }`}
          >
            <div className="min-w-0 pr-2">
              <h3
                id="regularise-checks-request-title"
                className={`text-sm font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}
              >
                Regularise checks Request
              </h3>
              <p className={`mt-0.5 truncate text-xs ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                Employee: {employeeName}
                {employeeId ? ` (ID: ${employeeId})` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className={`rounded p-1.5 shrink-0 transition ${
                darkMode ? "hover:bg-[#3C3C3C] text-gray-300" : "hover:bg-gray-100 text-gray-700"
              }`}
              aria-label="Close regularise checks"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {formBody}
        </div>
      </div>

      {showHistoryModal && (
        <div
          className="fixed inset-0 z-[10070] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onClick={() => setShowHistoryModal(false)}
        >
          <div
            className={`w-full max-w-lg max-h-[min(76vh,532px)] overflow-hidden flex flex-col rounded-lg shadow-xl border ${shellCls}`}
            role="dialog"
            aria-labelledby="reg-history-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${
                darkMode ? "border-[#3C3C3C]" : "border-gray-200"
              }`}
            >
              <div>
                <h3
                  id="reg-history-title"
                  className={`text-base font-semibold ${darkMode ? "text-gray-100" : "text-gray-900"}`}
                >
                  Regularised check dates
                </h3>
                <p className={`text-xs mt-0.5 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  {employeeName}
                  {employeeId ? ` · ID ${employeeId}` : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowHistoryModal(false)}
                className={`rounded p-1.5 ${darkMode ? "hover:bg-[#3C3C3C]" : "hover:bg-gray-100"}`}
                aria-label="Close history"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4">
              {historyRows.length === 0 ? (
                <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  No regularised check dates yet. Submit a request to add an entry here.
                </p>
              ) : (
                <ul
                  className={`divide-y rounded-md border ${
                    darkMode ? "divide-[#3C3C3C] border-[#3C3C3C]" : "divide-gray-200 border-gray-200"
                  }`}
                >
                  {historyRows.map((row) => (
                    <li
                      key={`${row.year}-${row.month}-${row.day}-${row.id}`}
                      className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-xs tabular-nums"
                    >
                      <span
                        className={`min-w-[5.5rem] font-semibold ${
                          darkMode ? "text-gray-100" : "text-gray-900"
                        }`}
                      >
                        {row.label}
                      </span>
                      <span>
                        <span className={`font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                          In
                        </span>{" "}
                        {row.checkIn ?? "—"}
                      </span>
                      <span>
                        <span className={`font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                          Out
                        </span>{" "}
                        {row.checkOut ?? "—"}
                      </span>
                      <span>
                        <span className={`font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                          Hrs
                        </span>{" "}
                        {row.totalHours ?? "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
